import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "@tanstack/react-router";
import {
  ArrowDownIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  PlusIcon,
  TrashIcon,
  XIcon,
} from "lucide-react";
import React from "react";
import {
  Controller,
  FormProvider,
  type SubmitHandler,
  useFieldArray,
  useForm,
  useFormContext,
} from "react-hook-form";

import * as schemas from "@blis/shared/schemas/workflow";
import { trpc } from "@blis/trpc/react";

import { Link } from "@/components/Link";
import { Button, ButtonGroup } from "@/components/ui/button";
import {
  ComboBox,
  ComboBoxInput,
  ComboBoxItem,
  ComboBoxList,
} from "@/components/ui/combobox";
import {
  FieldError,
  Input,
  Label,
  TextArea,
  TextField,
} from "@/components/ui/field";
import { Select, SelectList, SelectTrigger } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Text } from "@/components/ui/text";
import { mutationResponse } from "@/utils/trpc";
import type { WorkflowDetail } from "../../types";
import { AdminForm } from "../AdminForm";
import { TabLayout } from "../TabLayout";
import type { FormValues } from "./schema";
import { WorkflowActions } from "./WorkflowActions";
import { eventComponents } from "./WorkflowConditions";

export function WorkflowDetailForm({
  workflow,
}: {
  workflow?: WorkflowDetail;
}) {
  const methods = useForm<FormValues>({
    defaultValues: {
      id: workflow?.id,
      name: workflow?.name ?? "",
      description: workflow?.description ?? "",
      enabled: workflow?.enabled ?? true,
      trigger: workflow?.trigger,
      // biome-ignore lint/suspicious/noExplicitAny: lazy
      condition: workflow?.condition as any,
      // Server steps carry the full WorkflowActionType; the form only authors
      // the supported subset, so unsupported legacy actions load as-is and are
      // simply not selectable/editable.
      // biome-ignore lint/suspicious/noExplicitAny: legacy action types
      steps: (workflow?.steps ?? []) as any,
    },
    resolver: zodResolver(workflow ? schemas.update : schemas.create),
  });
  const navigate = useNavigate();

  const create = trpc.admin.workflow.create.useMutation();
  const update = trpc.admin.workflow.update.useMutation();

  const onSubmit: SubmitHandler<FormValues> = async (values) => {
    let p: Promise<unknown>;
    if (workflow) {
      p = update.mutateAsync(
        { id: workflow.id, ...values },
        mutationResponse({
          errorTitle: "Error updating workflow",
          successMessage: "Workflow updated",
        }),
      );
    } else {
      p = create.mutateAsync(
        values,
        mutationResponse({
          errorTitle: "Error adding workflow",
          successMessage: "Workflow added",
          onSuccess(data) {
            navigate({
              to: "/admin/workflows/$id/edit",
              params: {
                id: data.id,
              },
            });
          },
        }),
      );
    }
    await p;
  };

  return (
    <TabLayout>
      <FormProvider {...methods}>
        <AdminForm
          onSubmit={methods.handleSubmit(onSubmit)}
          id="add-workflow-form"
          className="max-w-5xl"
        >
          <div className="w-full flex flex-wrap gap-6">
            <div className="w-full lg:w-64 flex flex-col gap-4">
              <Controller
                control={methods.control}
                name="name"
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    onChange={(value) => field.onChange(value)}
                    isInvalid={!!error}
                  >
                    <Label>Name</Label>
                    <Input />
                    <FieldError>{error?.message}</FieldError>
                  </TextField>
                )}
              />
              <Controller
                control={methods.control}
                name="description"
                render={({
                  field: { ref, ...field },
                  fieldState: { error },
                }) => (
                  <TextField
                    {...field}
                    onChange={(value) => field.onChange(value)}
                    isInvalid={!!error}
                  >
                    <Label>Description</Label>
                    <TextArea rows={8} />
                    <FieldError>{error?.message}</FieldError>
                  </TextField>
                )}
              />

              <Controller
                control={methods.control}
                name="enabled"
                render={({ field }) => (
                  <div className="flex items-center gap-2">
                    <Switch
                      aria-label="Is enabled"
                      isSelected={field.value}
                      onChange={(value) => field.onChange(value)}
                    >
                      {field.value ? "Enabled" : "Disabled"}
                    </Switch>
                  </div>
                )}
              />
            </div>
            <Workflow />
          </div>
          <Separator className="my-4" />
          <div className="flex justify-start flex-row-reverse gap-2">
            <Button
              type="submit"
              isDisabled={create.isPending || update.isPending}
              isPending={create.isPending || update.isPending}
            >
              Save
            </Button>
            <Link to="/admin/workflows" variant="outline" asButton>
              Cancel
            </Link>
          </div>
        </AdminForm>
      </FormProvider>
    </TabLayout>
  );
}

function Workflow() {
  const { watch } = useFormContext<FormValues>();
  const trigger = watch("trigger");

  return (
    <div className="flex-1 flex flex-col gap-4">
      <EventSelect />
      {trigger && (
        <>
          <div className="flex items-center justify-center">
            <ArrowDownIcon className="size-5 text-muted-foreground" />
          </div>
          <EventConditions />
          <EventSteps />
        </>
      )}
    </div>
  );
}

function EventSelect() {
  const { control } = useFormContext<FormValues>();
  const eventOptions = Object.entries(schemas.events).map(([value, label]) => ({
    label,
    value,
  }));
  return (
    <div className="border rounded-lg p-4 mx-10">
      <Controller
        control={control}
        name="trigger"
        render={({ field, fieldState: { error } }) => (
          <ComboBox
            menuTrigger="focus"
            selectedKey={field.value ?? null}
            onSelectionChange={(value) => field.onChange(value)}
            onBlur={field.onBlur}
            isInvalid={!!error}
            validationBehavior="aria"
          >
            <Label>Event</Label>
            <ComboBoxInput placeholder="Select a trigger event" />
            <ComboBoxList items={eventOptions}>
              {(item) => (
                <ComboBoxItem id={item.value}>{item.label}</ComboBoxItem>
              )}
            </ComboBoxList>
            <FieldError>{error?.message}</FieldError>
          </ComboBox>
        )}
      />
    </div>
  );
}

function EventConditions() {
  const { control } = useFormContext<FormValues>();

  const { fields, append, remove, move } = useFieldArray<
    FormValues,
    "condition"
  >({
    control,
    name: "condition",
  });

  return (
    <div className="border border-dashed rounded-lg p-4 bg-accent-muted flex flex-col gap-2">
      {fields.map((field, index) => (
        <React.Fragment key={field.id}>
          {index > 0 && (
            <Text className="text-muted-foreground w-full text-center">
              AND
            </Text>
          )}
          <div className="border rounded-lg min-h-20 p-4 relative bg-background">
            <div className="flex gap-1 items-center justify-end absolute -top-3 right-2">
              <ButtonGroup>
                <Button
                  size="icon-sm"
                  type="button"
                  variant="outline"
                  className="bg-background opacity-100 disabled:bg-muted"
                  isDisabled={index === 0}
                  onPress={() => move(index, index - 1)}
                >
                  <ChevronUpIcon />
                </Button>
                <Button
                  size="icon-sm"
                  type="button"
                  variant="outline"
                  className="bg-background opacity-100 disabled:bg-muted"
                  isDisabled={index === fields.length - 1}
                  onPress={() => move(index, index + 1)}
                >
                  <ChevronDownIcon />
                </Button>
              </ButtonGroup>
              <Button
                size="icon-sm"
                type="button"
                variant="outline"
                className="bg-background"
                onPress={() => remove(index)}
              >
                <XIcon />
              </Button>
            </div>
            <EventAndCondition
              index={index}
              onLastRemoved={() => {
                if (fields.length === 1) {
                  remove(index);
                }
              }}
            />
          </div>
        </React.Fragment>
      ))}
      <div className="flex items-center justify-center">
        <Button
          size="sm"
          type="button"
          variant="ghost"
          onPress={() => {
            append({
              or: [
                {
                  fact: "",
                  operator: "eq",
                  value: "",
                },
              ],
            });
          }}
        >
          <PlusIcon />
          Add condition
        </Button>
      </div>
    </div>
  );
}

function EventAndCondition({
  index,
  onLastRemoved,
}: {
  index: number;
  onLastRemoved: () => void;
}) {
  const { control } = useFormContext<FormValues>();
  const { fields, append, remove } = useFieldArray<FormValues>({
    control,
    name: `condition.${index}.or`,
  });

  return (
    <>
      <div className="grid grid-cols-10 items-center gap-2">
        {fields.map((field, orIndex) => (
          <React.Fragment key={field.id}>
            <Text elementType="p" className="w-14 shrink-0">
              {orIndex === 0 ? "When" : "Or"}
            </Text>

            <EventOrComponent parentIndex={index} index={orIndex} />
            <Button
              size="icon-sm"
              type="button"
              variant="ghost"
              isDisabled={fields.length === 1}
              onPress={() => {
                remove(index);
                if (fields.length === 1) {
                  onLastRemoved();
                }
              }}
            >
              <TrashIcon />
            </Button>
          </React.Fragment>
        ))}
      </div>
      <Button
        size="sm"
        type="button"
        variant="ghost"
        onPress={() => {
          append({
            fact: "",
            operator: "eq",
            value: "",
          });
        }}
      >
        <PlusIcon />
        OR
      </Button>
    </>
  );
}

function EventOrComponent({
  index,
  parentIndex,
}: {
  index: number;
  parentIndex: number;
}) {
  const { control, watch } = useFormContext<FormValues>();
  const trigger = watch("trigger");

  if (!trigger) {
    return null;
  }

  const C = eventComponents[trigger];

  return (
    <>
      <Controller
        control={control}
        name={`condition.${parentIndex}.or.${index}.fact`}
        render={({ field: { ref, ...field }, fieldState: { error } }) => (
          <Select
            {...field}
            aria-label={`Fact for condition ${index} of ${parentIndex}`}
            selectedKey={field.value ?? null}
            onSelectionChange={field.onChange}
            isInvalid={!!error}
            className="col-span-5 sm:col-span-3"
          >
            <SelectTrigger />
            <SelectList>
              <C.Facts />
            </SelectList>
            <FieldError>{error?.message}</FieldError>
          </Select>
        )}
      />
      <div className="col-span-5 sm:col-span-2">
        <Controller
          control={control}
          name={`condition.${parentIndex}.or.${index}.operator`}
          render={({ field: { ref, ...field }, fieldState: { error } }) => (
            <Select
              {...field}
              aria-label={`Operator for condition ${index} of ${parentIndex}`}
              selectedKey={field.value ?? null}
              onSelectionChange={field.onChange}
              isInvalid={!!error}
              className="col-span-5 sm:col-span-2"
            >
              <SelectTrigger />
              <SelectList>
                <C.Operators parentIndex={parentIndex} index={index} />
              </SelectList>
              <FieldError>{error?.message}</FieldError>
            </Select>
          )}
        />
      </div>
      <div className="col-span-5 sm:col-span-3">
        <C.Value parentIndex={parentIndex} index={index} />
      </div>
    </>
  );
}

function EventSteps() {
  const { control } = useFormContext();

  const { fields, append, remove } = useFieldArray({
    name: "steps",
    control,
    keyName: "fieldId",
  });

  return (
    <>
      {fields.map((field, index) => (
        <React.Fragment key={field.fieldId}>
          <div className="flex items-center justify-center">
            <ArrowDownIcon className="size-5 text-muted-foreground" />
          </div>
          <div
            className="border rounded-lg p-4 relative flex flex-col gap-4 mx-10"
            key={field.fieldId}
          >
            <Button
              size="icon-sm"
              type="button"
              variant="ghost"
              className="absolute top-2 right-2"
              onPress={() => remove(index)}
            >
              <XIcon />
            </Button>
            <ActionSelect index={index} />
            <WorkflowActions index={index} />
          </div>
        </React.Fragment>
      ))}
      <div className="flex flex-col gap-4 items-center justify-center">
        <ArrowDownIcon className="size-5 text-muted-foreground" />
        <Button
          type="button"
          variant="outline"
          className="border-dashed shadow-none"
          onPress={() => append({})}
        >
          <PlusIcon />
          Add step
        </Button>
      </div>
    </>
  );
}

function ActionSelect({ index }: { index: number }) {
  const { control } = useFormContext();
  const options = Object.entries(schemas.actions).map(([value, label]) => ({
    label,
    value,
  }));
  return (
    <Controller
      control={control}
      name={`steps.${index}.action`}
      render={({ field, fieldState: { error } }) => (
        <ComboBox
          menuTrigger="focus"
          selectedKey={field.value ?? null}
          onSelectionChange={(value) => field.onChange(value)}
          isInvalid={!!error}
          validationBehavior="aria"
        >
          <Label>Action</Label>
          <ComboBoxInput />
          <ComboBoxList items={options}>
            {(item) => (
              <ComboBoxItem id={item.value}>{item.label}</ComboBoxItem>
            )}
          </ComboBoxList>
          <FieldError>{error?.message}</FieldError>
        </ComboBox>
      )}
    />
  );
}
